<template>
  <div v-if="active && curStep" class="guide-overlay">
    <!-- 高亮框：用巨大的 box-shadow 把四周压暗，形成聚光灯效果 -->
    <div class="guide-highlight" :style="highlightStyle"></div>
    <!-- 提示气泡 -->
    <div ref="bubble" class="guide-bubble" :style="bubbleStyle">
      <div v-if="arrowVisible" class="guide-arrow" :style="arrowStyle"></div>
      <a class="guide-close" title="关闭指引" @click="finish">&times;</a>
      <div class="guide-bubble-title">
        <span class="guide-bubble-name">{{ curStep.title }}</span>
        <span class="guide-bubble-step">{{ index + 1 }}/{{ steps.length }}</span>
      </div>
      <div class="guide-bubble-content">{{ curStep.content }}</div>
      <div class="guide-bubble-actions">
        <button v-if="index > 0" type="button" class="guide-btn guide-btn-ghost" @click="go(-1)">上一个</button>
        <button type="button" class="guide-btn guide-btn-primary" @click="go(1)">{{ isLast ? '完成' : '下一个' }}</button>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * 新手指引浮层：区域高亮 + 气泡提示。
 * - steps: [{ title, content, selector }]，selector 支持逗号分隔的多个候选（取第一个可见元素），
 *   selector 为空时展示居中欢迎气泡（无高亮）。
 * - 纯展示组件：是否显示由父组件查询后端指引记录决定，关闭/完成时向上抛 finish 事件由父组件落库。
 */
export default {
  name: 'GuideOverlay',
  props: {
    steps: {
      type: Array,
      default: function () { return []; }
    }
  },
  data() {
    return {
      active: false,
      index: 0,
      // 高亮框样式（相对视口 fixed）
      highlightStyle: { display: 'none' },
      bubbleStyle: {},
      arrowVisible: false,
      arrowStyle: {},
      // 移动端讲解抽屉内按钮时由指引主动开抽屉，离开/结束时收回
      drawerOpenedByGuide: false
    };
  },
  computed: {
    curStep: function () {
      return this.steps[this.index] || null;
    },
    isLast: function () {
      return this.index === this.steps.length - 1;
    }
  },
  watch: {
    // 任务链文案等异步更新步骤内容时，重算定位
    steps: {
      deep: true,
      handler: function () {
        if (this.active) this.update();
      }
    }
  },
  mounted: function () {
    if (this.steps.length === 0) return;
    this.active = true;
    this.update();
    window.addEventListener('resize', this.update);
    // capture 捕获任意容器的滚动（页面局部滚动也触发重定位）
    window.addEventListener('scroll', this.update, true);
    window.addEventListener('keydown', this.onKeydown);
  },
  beforeDestroy: function () {
    window.removeEventListener('resize', this.update);
    window.removeEventListener('scroll', this.update, true);
    window.removeEventListener('keydown', this.onKeydown);
  },
  methods: {
    onKeydown: function (e) {
      if (e.key === 'Escape' || e.keyCode === 27) this.finish();
    },
    // dir=1 下一步 / dir=-1 上一步，越界即完成
    go: function (dir) {
      const next = this.index + dir;
      if (next < 0 || next >= this.steps.length) { this.finish(); return; }
      this.index = next;
      this.update();
    },
    finish: function () {
      this.active = false;
      if (this.drawerOpenedByGuide) {
        this.drawerOpenedByGuide = false;
        this.$root.eventHub.$emit('closeDrawer');
      }
      this.$emit('finish');
    },
    // 讲解抽屉内的功能按钮（openDrawer: true 的步骤）时先滑出侧边栏，
    // 等抽屉动画结束后再定位；离开该类步骤或结束时收回抽屉。桌面端左栏常驻，直接跳过。
    applyDrawerState: function (done) {
      const step = this.curStep;
      const needDrawer = !!(step && step.openDrawer);
      const isMobile = document.documentElement.clientWidth <= 768;
      if (needDrawer && isMobile) {
        if (!this.drawerOpenedByGuide) {
          this.drawerOpenedByGuide = true;
          this.$root.eventHub.$emit('openDrawer');
          setTimeout(done, 450);
          return;
        }
      } else if (this.drawerOpenedByGuide) {
        this.drawerOpenedByGuide = false;
        this.$root.eventHub.$emit('closeDrawer');
        setTimeout(done, 350);
        return;
      }
      done();
    },
    // selector 支持逗号分隔候选，返回第一个有实际尺寸的可见元素
    resolveTarget: function (selector) {
      if (!selector) return null;
      const list = selector.split(',');
      for (let i = 0; i < list.length; i++) {
        const sel = list[i].trim();
        if (!sel) continue;
        let els = [];
        try { els = document.querySelectorAll(sel); } catch (e) { continue; }
        for (let j = 0; j < els.length; j++) {
          const rect = els[j].getBoundingClientRect();
          if (rect.width > 0 && rect.height > 0) return els[j];
        }
      }
      return null;
    },
    update: function () {
      if (!this.active || !this.curStep) return;
      this.applyDrawerState(() => {
        if (!this.active || !this.curStep) return;
        this.$nextTick(() => {
          if (!this.active || !this.curStep) return;
          this.locate();
        });
      });
    },
    locate: function () {
      const el = this.resolveTarget(this.curStep.selector);
        const winW = document.documentElement.clientWidth;
        const winH = document.documentElement.clientHeight;
        const bubble = this.$refs.bubble;

        if (el) {
          const pad = 6;
          const r = el.getBoundingClientRect();
          this.highlightStyle = {
            display: 'block',
            left: (r.left - pad) + 'px',
            top: (r.top - pad) + 'px',
            width: (r.width + pad * 2) + 'px',
            height: (r.height + pad * 2) + 'px'
          };
        } else {
          // 目标元素缺失（如移动端隐藏的罗盘）：不高亮，气泡居中，保证指引始终可读
          this.highlightStyle = { display: 'none' };
        }

        if (!bubble) return;
        const bw = bubble.offsetWidth;
        const bh = bubble.offsetHeight;
        let left = 0;
        let top = 0;
        let arrow = null; // {side, offset}

        if (el) {
          const r = el.getBoundingClientRect();
          const gap = 14;
          const arrowGap = 22; // 预留箭头空间
          if (winH - r.bottom > bh + arrowGap) {
            // 下方
            top = r.bottom + gap;
            left = r.left + r.width / 2 - bw / 2;
            arrow = { side: 'top', offset: r.left + r.width / 2 - left };
          } else if (r.top > bh + arrowGap) {
            // 上方
            top = r.top - bh - gap;
            left = r.left + r.width / 2 - bw / 2;
            arrow = { side: 'bottom', offset: r.left + r.width / 2 - left };
          } else if (winW - r.right > bw + arrowGap) {
            // 右侧
            left = r.right + gap;
            top = r.top + r.height / 2 - bh / 2;
            arrow = { side: 'left', offset: 0 };
          } else if (r.left > bw + arrowGap) {
            // 左侧
            left = r.left - bw - gap;
            top = r.top + r.height / 2 - bh / 2;
            arrow = { side: 'right', offset: 0 };
          } else {
            // 实在放不下：盖在下方并尽量避开
            top = Math.min(r.bottom + gap, winH - bh - 8);
            left = r.left + r.width / 2 - bw / 2;
            arrow = { side: 'top', offset: r.left + r.width / 2 - left };
          }
          // 箭头横向位置收进气泡内
          if (arrow && arrow.side !== 'left' && arrow.side !== 'right') {
            arrow.offset = Math.max(24, Math.min(bw - 24, arrow.offset));
          }
        } else {
          left = (winW - bw) / 2;
          top = (winH - bh) / 2;
        }

        // 视口钳制
        left = Math.max(8, Math.min(left, winW - bw - 8));
        top = Math.max(8, Math.min(top, winH - bh - 8));

        this.bubbleStyle = { left: left + 'px', top: top + 'px' };
        if (arrow) {
          this.arrowVisible = true;
          this.arrowStyle = this.buildArrowStyle(arrow);
        } else {
          this.arrowVisible = false;
          this.arrowStyle = {};
        }
    },
    buildArrowStyle: function (arrow) {
      const style = {};
      if (arrow.side === 'top') {
        style.top = '-7px';
        style.left = arrow.offset + 'px';
      } else if (arrow.side === 'bottom') {
        style.bottom = '-7px';
        style.left = arrow.offset + 'px';
      } else if (arrow.side === 'left') {
        style.left = '-7px';
        style.top = '20px';
      } else {
        style.right = '-7px';
        style.top = '20px';
      }
      return style;
    }
  }
};
</script>

<style scoped>
.guide-overlay {
  position: fixed;
  left: 0;
  top: 0;
  width: 100vw;
  height: 100vh;
  z-index: 3000;
  /* 透明容器本身拦截页面点击，暗色遮罩由高亮框的 box-shadow 呈现 */
}

.guide-highlight {
  position: fixed;
  border-radius: 8px;
  box-shadow: 0 0 0 2px #ffd054, 0 0 0 9999px rgba(17, 17, 17, 0.68);
  transition: all 0.25s ease;
  pointer-events: none;
}

.guide-bubble {
  position: fixed;
  max-width: 340px;
  min-width: 220px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.28), 0 0 0 1px rgba(0, 0, 0, 0.05);
  padding: 14px 16px 12px;
  box-sizing: border-box;
  transition: left 0.25s ease, top 0.25s ease;
  z-index: 1;
}

.guide-arrow {
  position: absolute;
  width: 14px;
  height: 14px;
  background: #fff;
  transform: rotate(45deg);
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.05);
}

.guide-arrow[style*="top: -7px"] {
  box-shadow: -1px -1px 0 0 rgba(0, 0, 0, 0.05);
}

.guide-bubble-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  padding-right: 20px;
}

.guide-bubble-name {
  font-size: 15px;
  font-weight: bold;
  color: #303133;
}

.guide-bubble-step {
  flex-shrink: 0;
  font-size: 12px;
  color: #909399;
  background: #f0f2f5;
  border-radius: 10px;
  padding: 1px 8px;
}

.guide-bubble-content {
  font-size: 13px;
  line-height: 1.7;
  color: #606266;
  white-space: pre-line;
  word-break: break-all;
}

.guide-bubble-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
}

.guide-btn {
  padding: 5px 14px;
  font-size: 13px;
  border-radius: 5px;
  cursor: pointer;
  line-height: 1.4;
}

.guide-btn-ghost {
  background: #fff;
  border: 1px solid #dcdfe6;
  color: #606266;
}

.guide-btn-ghost:hover {
  color: #409eff;
  border-color: #c6e2ff;
  background: #ecf5ff;
}

.guide-btn-primary {
  background: #409eff;
  border: 1px solid #409eff;
  color: #fff;
}

.guide-btn-primary:hover {
  background: #66b1ff;
  border-color: #66b1ff;
}

.guide-close {
  position: absolute;
  right: 10px;
  top: 8px;
  font-size: 18px;
  line-height: 1;
  color: #c0c4cc;
  cursor: pointer;
  text-decoration: none;
}

.guide-close:hover {
  color: #909399;
}

/* 移动端：气泡贴边更宽 */
@media (max-width: 768px) {
  .guide-bubble {
    max-width: calc(100vw - 24px);
    min-width: 200px;
  }
}
</style>
